// src/components/pediatric/TeleAuscultationTab.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AuscultationPanel from "./AuscultationPanel";
import "../../css/TeleAuscultationTab.css";
import api from "../../api/axiosConfig";
import { useAuth } from "../../context/AuthContext";

const AUSCULTATION_DATA = [
  { icon: "🫁", title: "Lungs Auscultation", key: "lungs" },
  { icon: "❤️", title: "Heart Auscultation", key: "heart" },
  { icon: "〰️", title: "Arteries Auscultation", key: "arteries" },
  { icon: "🩺", title: "Abdomen Auscultation", key: "abdomen" },
];

interface Props {
  onSuccess: () => void;
}

const TeleAuscultationTab: React.FC<Props> = ({ onSuccess }) => {
  const { emrNumber } = useParams<{ emrNumber: string }>();
  const { doctor } = useAuth();

  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [openPanels, setOpenPanels] = useState<string[]>([]);
  const [formData, setFormData] = useState<
    Record<string, { assessmentNote: string; hasConcerns: boolean | null }>
  >({});
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [abdominalData, setAbdominalData] = useState<
    {
      name: string;
      finding: string;
      pain: boolean;
      mass: boolean;
    }[]
  >([]);

  /* ---------------- FETCH & PRESIGN AUDIO ---------------- */
  useEffect(() => {
    if (!emrNumber) return;

    const loadAuscultationAudio = async () => {
      try {
        const res = await api.dataCollectionApi.get(
          `/api/forms/vitals/${emrNumber}`,
        );

        const auscultation = res.data?.data?.auscultation;
        if (!auscultation) return;

        /* ---------------- ABDOMINAL PALPATION ---------------- */
        const palpation = res.data?.data?.["Abdominal Palpation"];

        if (palpation) {
          const regions = Object.keys(palpation).map((key) => ({
            name: key,
            finding: palpation[key].Finding,
            pain: palpation[key].Pain || false,
            mass: palpation[key].Mass || false,
          }));

          setAbdominalData(regions);
        }

        const audioMap: Record<string, string[]> = {
          lungs: auscultation.lungAudioUrls || [],
          heart: auscultation.heartAudioUrls || [],
          arteries: auscultation.arteriesAudioUrls || [],
          abdomen: auscultation.abdomenAudioUrls || [],
        };

        const signedUrls: Record<string, string> = {};

        for (const section of Object.keys(audioMap)) {
          const fullPath = audioMap[section][0];
          if (!fullPath) continue;

          const parts = fullPath.split("/");
          const module = `${parts[0]}/${parts[1]}/${parts[2]}`;
          const key = parts.slice(3).join("/");

          const presign = await api.S3config.get("/view-url", {
            params: { module, key },
            headers: { "x-internal-token": "super-secret-password" },
          });

          if (presign.data?.url) {
            signedUrls[section] = presign.data.url;
          }
        }

        setAudioUrls(signedUrls);
      } catch (err) {
        console.error("Failed to load auscultation audio:", err);
      }
    };

    loadAuscultationAudio();
  }, [emrNumber]);

  /* ---------------- FETCH CURRENT USER DATA ---------------- */

  /* ---------------- PANEL DATA ---------------- */
  const handlePanelChange = (key: string, data: any) => {
    setFormData((prev) => ({ ...prev, [key]: data }));
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async () => {
    const missing = AUSCULTATION_DATA.filter(
      (s) =>
        !formData[s.key] ||
        !formData[s.key].assessmentNote ||
        formData[s.key].hasConcerns === null,
    );

    if (missing.length > 0) {
      setErrors(missing.map((m) => m.key));
      alert("Please complete all auscultation sections.");
      return;
    }

    if (!doctor) {
      alert("Doctor information missing. Please login again.");
      return;
    }

    setSubmitting(true);

    try {
      await api.dataCollectionApi.post("/api/teledocs/tele-auscultation", {
        emrId: emrNumber,
        doctorName: doctor ? `Dr. ${doctor.name}` : null,
        sections: {
          lungs: formData.lungs,
          heart: formData.heart,
          arteries: formData.arteries,
          abdomen: formData.abdomen,
        },
      });

      onSuccess(); // 👉 Move to Modified Asthma Predictive Index
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- UI CONTROLS ---------------- */
  const togglePanel = (key: string) => {
    setOpenPanels((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const expandAll = () =>
    setOpenPanels(AUSCULTATION_DATA.map((item) => item.key));

  const collapseAll = () => setOpenPanels([]);

  return (
    <div className="tele-auscultation-container">
      <div className="tab-header">
        <h3>Tele-Auscultation</h3>
        <div className="global-controls">
          <button onClick={expandAll}>Expand All</button>
          <button onClick={collapseAll}>Collapse All</button>
        </div>
      </div>

      <div className="accordion-container">
        {AUSCULTATION_DATA.map((item) => (
          <AuscultationPanel
            key={item.key}
            icon={item.icon}
            title={item.title}
            panelKey={item.key}
            audioUrl={audioUrls[item.key]}
            isOpen={openPanels.includes(item.key)}
            onToggle={() => togglePanel(item.key)}
            onChange={handlePanelChange}
            hasError={errors.includes(item.key)}
            abdominalData={item.key === "abdomen" ? abdominalData : []}
          />
        ))}
      </div>

      {/* ---------------- ABDOMINAL EXAMINATION ---------------- */}
      {/* <div className="abdominal-section">
        <h3>Abdominal Examination (9 Regions)</h3>

        {abdominalData.map((region, index) => (
          <div key={index} className="abdominal-card">
            <p>
              <strong>
                {index + 1}. {region.name}
              </strong>
            </p>

            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  checked={region.finding === "Expected Findings"}
                  readOnly
                />
                Expected Findings
              </label>

              <div className="unexpected-block">
                <label>
                  <input
                    type="radio"
                    checked={region.finding === "Unexpected Findings"}
                    readOnly
                  />
                  Unexpected Findings
                </label>

                {region.finding === "Unexpected Findings" && (
                  <div className="checkbox-group">
                    <label>
                      <input type="checkbox" checked={region.pain} readOnly />
                      Pain
                    </label>

                    <label>
                      <input type="checkbox" checked={region.mass} readOnly />
                      Mass
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div> */}

      <div className="footer-bar">
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: "50%" }} />
          </div>
          <span>2 of 3 steps completed</span> 
        </div>
        <div className="action-buttons">
          <button className="reset-btn">Reset</button>
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeleAuscultationTab;
